/**
 * Surprav Cart Drawer Web Component
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.closeButtons = this.querySelectorAll('[data-close-drawer]');
    this._triggerElement = null;
    this._removeTrapFocus = null;
    
    this.closeButtons.forEach(btn => btn.addEventListener('click', () => this.close()));
    
    // Close on Escape key
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    // Listen for cart updates
    if (typeof subscribe === 'function') {
      subscribe('cart:updated', (cart) => this.render(cart));
    }

    // Listen for manual triggers
    document.querySelectorAll('[data-cart-trigger]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this._triggerElement = btn;
        this.open();
      });
    });
  }

  /**
   * Set the element that triggered the drawer opening.
   * Used by external components (e.g., product-form quick-add) to
   * ensure focus returns to the correct element on close.
   */
  setActiveElement(element) {
    this._triggerElement = element;
  }

  /**
   * Sections product-form.js asks the cart/add API to render.
   * Required so product-form's `getSectionsToRender().map(...)` doesn't throw
   * (which previously aborted add-to-cart before the fetch even started).
   */
  getSectionsToRender() {
    return [
      { id: 'cart-drawer', section: 'cart-drawer', selector: '[data-cart-inner]' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '#cart-icon-bubble' },
    ];
  }

  /**
   * Called by product-form.js after a successful add. `response` is the
   * cart/add.js payload (a cart object). Reuse render() to refresh + open.
   */
  renderContents(response) {
    this.render(response);
  }

  async render(cart) {
    // If cart is passed, we might still want to fetch the sections for the HTML
    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const response = await fetch(`${window.location.pathname}?sections=${sections.join(',')}`);
    const data = await response.json();

    if (data['cart-drawer']) {
      const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
      const inner = html.querySelector('[data-cart-inner]');
      const footer = html.querySelector('.cart-drawer__footer');
      
      this.querySelector('[data-cart-inner]').innerHTML = inner.innerHTML;
      
      const currentFooter = this.querySelector('.cart-drawer__footer');
      if (footer && currentFooter) {
        currentFooter.innerHTML = footer.innerHTML;
      } else if (footer) {
        this.querySelector('.cart-drawer__content').appendChild(footer);
      } else if (currentFooter) {
        currentFooter.remove();
      }

      // Update header/mobile nav badges
      const cartCounts = document.querySelectorAll('[data-cart-count]');
      cartCounts.forEach(el => {
        el.textContent = cart.item_count;
        el.setAttribute('data-cart-count', cart.item_count);
      });
    }

    this.open();
  }

  open() {
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('overflow-hidden');

    // Activate focus trap within the drawer content
    const content = this.querySelector('.cart-drawer__content');
    if (content && Surprav.trapFocus) {
      this._removeTrapFocus = Surprav.trapFocus(content, 'cartDrawer');
    }

    // Focus the close button for immediate keyboard access
    const closeBtn = this.querySelector('.cart-drawer__close');
    if (closeBtn) closeBtn.focus();
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');

    // Remove focus trap
    if (this._removeTrapFocus) {
      this._removeTrapFocus();
      this._removeTrapFocus = null;
    }

    // Return focus to the element that triggered the drawer
    if (this._triggerElement && typeof this._triggerElement.focus === 'function') {
      this._triggerElement.focus();
    }
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
